// Central keyboard + gamepad input hub for the multi-tile player.
//
// Couch co-op runs several emulator tiles in one page, each its own
// "player." A single physical keyboard is shared across all of them, so
// we CANNOT give each tile its own window listener — they'd all see
// every keystroke. Instead one InputHub owns the window keydown/keyup
// listeners and the gamepad poll, and *demuxes*: each keypress is
// routed to the one player whose profile binds that code (WASD -> P1,
// arrows -> P2, …), and each assigned gamepad is polled for its owner.
//
// Per player we track a Set of pressed "sources" per NES button and
// only fire a transition when the merged state (any source held) flips
// — so a player holding Z and tapping their pad's A still leaves NES A
// held when the pad releases. Sources are namespaced per player, so two
// players pressing "their A" never collide.
//
// Bindings (keyboard map, gamepad map, pad assignment) live in
// bindings.ts and are user-editable via the controls modal. Stick-axis
// -> D-pad bindings are not user-editable; they live here as a fixed
// deadzone mapping, applied to whichever pad a player owns.
//
// NES button numbering: A=0 B=1 Select=2 Start=3 Up=4 Down=5 Left=6 Right=7.

import {
  getProfiles, getGamepadMap, getPadIndex, setPadIndex,
} from "./bindings.js";

export type ButtonHandler = (button: number, down: boolean) => void;

const STICK_DEADZONE = 0.5;

// True when the event's target is a typing surface — an editable
// input/textarea or a contentEditable element. Used to keep mapped
// controller keys from being swallowed when the user is typing
// (e.g. into the devtools selector-expression textarea, where "X"
// would otherwise fire the A button instead of typing the letter).
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

export class InputHub {
  // Per-player button handler (the tile's host.buttonDown/Up bridge).
  // Sparse: keyed by player slot, since middle tiles can be removed.
  private handlers = new Map<number, ButtonHandler>();
  // Per player: 8 NES buttons, each a Set of currently-active sources.
  private sources = new Map<number, Array<Set<string>>>();
  // Keyboard codes currently held. A code belongs to exactly one player
  // (bindings enforces this), so a single global set suffices for the
  // press/release de-dup.
  private heldKeys = new Set<string>();
  private rafHandle: number | null = null;
  private stopped = false;
  private enabled = true;

  start(): void {
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("gamepadconnected", this.onGamepadConnected);
    window.addEventListener("gamepaddisconnected", this.onGamepadDisconnected);
    this.rafHandle = requestAnimationFrame(this.pollGamepads);
  }

  stop(): void {
    this.stopped = true;
    if (this.rafHandle !== null) cancelAnimationFrame(this.rafHandle);
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("gamepadconnected", this.onGamepadConnected);
    window.removeEventListener("gamepaddisconnected", this.onGamepadDisconnected);
    this.releaseAll();
  }

  // Register (or replace) the handler for a player slot. Called by the
  // tile manager after a tile's host comes up.
  setHandler(player: number, handler: ButtonHandler): void {
    this.handlers.set(player, handler);
    if (!this.sources.has(player)) {
      this.sources.set(player, Array.from({ length: 8 }, () => new Set<string>()));
    }
  }

  // Drop a player slot (tile closed). Releases anything it was holding
  // so the (now-stopping) host doesn't keep a phantom button down.
  removeHandler(player: number): void {
    this.releasePlayer(player);
    this.handlers.delete(player);
    this.sources.delete(player);
  }

  // Temporarily stop forwarding events to the emulators while the user
  // is remapping bindings in the controls modal. On disable: tell the
  // emulator every button is up (so a key down at pause time isn't stuck
  // down). On re-enable: re-press any key still physically held — the OS
  // sends no fresh keydown for an already-held key, so without this a held
  // button (especially a non-repeating modifier) would stay dead until
  // physically re-pressed.
  setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    if (enabled) this.repressHeld();
    else this.releaseAll();
  }

  // Claim any connected-but-unowned gamepad for the lowest-numbered
  // active player that has no pad yet. Called on gamepadconnected and
  // whenever the tile set changes (so adding a tile picks up a pad that
  // was plugged in earlier with no slot to go to).
  claimUnassignedPads(): void {
    const pads = navigator.getGamepads ? navigator.getGamepads() : [];
    const owned = new Set<number>();
    for (const p of getProfiles()) {
      if (p.padIndex !== null) owned.add(p.padIndex);
    }
    const freeSlots = Array.from(this.handlers.keys())
      .filter(player => getPadIndex(player) === null)
      .sort((a, b) => a - b);
    let next = 0;
    for (const pad of pads) {
      if (!pad || !pad.connected || owned.has(pad.index)) continue;
      if (next >= freeSlots.length) break;
      setPadIndex(freeSlots[next]!, pad.index);
      owned.add(pad.index);
      next++;
    }
  }

  // Which (player, NES button) a keyboard code drives, or null. Only
  // *active* players (those with a registered handler) claim a code —
  // a key bound to a closed tile's profile passes through to the page.
  private ownerOfCode(code: string): { player: number; btn: number } | null {
    const profiles = getProfiles();
    for (let player = 0; player < profiles.length; player++) {
      if (!this.handlers.has(player)) continue;
      const btn = profiles[player]!.keyboard[code];
      if (btn !== undefined) return { player, btn };
    }
    return null;
  }

  private setSource(player: number, btn: number, source: string, down: boolean): void {
    const arr = this.sources.get(player);
    if (!arr) return;
    const set = arr[btn]!;
    const before = set.size > 0;
    if (down) set.add(source); else set.delete(source);
    const after = set.size > 0;
    if (before === after) return;
    this.handlers.get(player)?.(btn, after);
  }

  private releasePlayer(player: number): void {
    const arr = this.sources.get(player);
    const handler = this.handlers.get(player);
    if (arr) {
      for (let btn = 0; btn < 8; btn++) {
        const set = arr[btn]!;
        if (set.size > 0) {
          set.clear();
          handler?.(btn, false);
        }
      }
    }
    // Drop any held keyboard codes this player owned.
    const profile = getProfiles()[player];
    if (profile) {
      for (const code of Object.keys(profile.keyboard)) this.heldKeys.delete(code);
    }
  }

  // Tell the emulator every held button is up, WITHOUT clearing heldKeys.
  // heldKeys tracks PHYSICAL key state, which survives the controls modal
  // so a key still down on re-enable can be re-pressed (repressHeld);
  // onKeyUp keeps pruning heldKeys while disabled, so a key released during
  // the modal is correctly dropped and not falsely replayed.
  private releaseAll(): void {
    for (const player of this.handlers.keys()) {
      const arr = this.sources.get(player);
      if (!arr) continue;
      for (let btn = 0; btn < 8; btn++) {
        const set = arr[btn]!;
        if (set.size > 0) {
          set.clear();
          this.handlers.get(player)?.(btn, false);
        }
      }
    }
  }

  // Re-drive the emulator for every key still physically held, mapping each
  // through its CURRENT owner (a binding may have changed in the modal).
  // Called on re-enable; pairs with releaseAll keeping heldKeys intact.
  private repressHeld(): void {
    for (const code of this.heldKeys) {
      const owner = this.ownerOfCode(code);
      if (owner) this.setSource(owner.player, owner.btn, `kbd:${code}`, true);
    }
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (!this.enabled) return;
    // Keys typed into an input / textarea / contentEditable surface
    // belong to that surface, not the emulator.
    if (isEditableTarget(e.target)) return;
    const owner = this.ownerOfCode(e.code);
    if (!owner) return;
    e.preventDefault();
    if (this.heldKeys.has(e.code)) return;
    this.heldKeys.add(e.code);
    this.setSource(owner.player, owner.btn, `kbd:${e.code}`, true);
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    const owner = this.ownerOfCode(e.code);
    if (!this.enabled) {
      // Still clear bookkeeping so a release isn't missed once enabled.
      this.heldKeys.delete(e.code);
      return;
    }
    if (!owner) return;
    e.preventDefault();
    if (!this.heldKeys.has(e.code)) return;
    this.heldKeys.delete(e.code);
    this.setSource(owner.player, owner.btn, `kbd:${e.code}`, false);
  };

  private onGamepadConnected = (_e: GamepadEvent): void => {
    this.claimUnassignedPads();
  };
  private onGamepadDisconnected = (_e: GamepadEvent): void => {};

  private pollGamepads = (): void => {
    if (this.stopped) return;
    if (!this.enabled) {
      this.rafHandle = requestAnimationFrame(this.pollGamepads);
      return;
    }
    const pads = navigator.getGamepads ? navigator.getGamepads() : [];
    for (const player of this.handlers.keys()) {
      const arr = this.sources.get(player);
      if (!arr) continue;
      const padIndex = getPadIndex(player);
      const pad = padIndex !== null ? pads[padIndex] ?? null : null;

      // The set of pad sources that should be down for this player.
      const desired: Record<string, number> = Object.create(null) as Record<string, number>;
      if (pad && pad.connected) {
        const gamepadMap = getGamepadMap(player);
        for (const idxStr of Object.keys(gamepadMap)) {
          const padBtn = Number(idxStr);
          const b = pad.buttons[padBtn];
          if (b && b.pressed) desired[`pad:b${padBtn}`] = gamepadMap[padBtn]!;
        }
        const ax = pad.axes[0] ?? 0;
        const ay = pad.axes[1] ?? 0;
        if (ax < -STICK_DEADZONE) desired["pad:axL"] = 6;
        if (ax >  STICK_DEADZONE) desired["pad:axR"] = 7;
        if (ay < -STICK_DEADZONE) desired["pad:axU"] = 4;
        if (ay >  STICK_DEADZONE) desired["pad:axD"] = 5;
      }
      // Release pad sources no longer desired for this player.
      for (let btn = 0; btn < 8; btn++) {
        for (const s of Array.from(arr[btn]!)) {
          if (s.startsWith("pad:") && !(s in desired)) {
            this.setSource(player, btn, s, false);
          }
        }
      }
      // Press anything new.
      for (const s of Object.keys(desired)) {
        this.setSource(player, desired[s]!, s, true);
      }
    }
    this.rafHandle = requestAnimationFrame(this.pollGamepads);
  };
}
